import { FC, ReactElement, useEffect, useMemo, useState, Component } from "react";
import { Box, Button, styled } from "@mui/material";
import { DateRange as DateRangeType, Range, RangeKeyDict } from "react-date-range";
import { getLastYearToDateRange } from "@/utils";

// Cast DateRange to a React Component type to resolve the refs issue
const DateRange = DateRangeType as unknown as typeof Component;

import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file

import DialogMenu from "../dialog-menu";
import { PRESET_RANGES } from "@/constants";

const DateRangePickerWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column" /* Adjust for smaller screens */,
  background: "white",
  border: "1px solid #eaecf0",
  borderRadius: "8px",

  "@media (min-width: 870px)": {
    flexDirection: "row" /* Adjust for larger screens */,
  },
}));

const Sidebar = styled("div")(() => ({
  padding: "12px 1rem",
  borderRight: "1px solid #eaecf0",
  width: "100%" /* Full width for smaller screens */,

  "@media (min-width: 870px)": {
    width: "190px" /* Fixed width for larger screens */,
  },

  ul: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "4px",
    borderBottom: "1px solid #eaecf0",
    paddingBottom: "1rem",

    "@media (min-width: 870px)": {
      display: "flex",
      flexDirection: "column",
      borderBottom: 0,
      paddingBottom: 0,
    },

    "@media (max-width: 556px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },

    "@media (max-width: 420px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },

  button: {
    background: "none",
    border: "none",
    color: "#000000",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 500,
    padding: "10px 1rem",
    textAlign: "left",
    width: "100%",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",

    "&:hover, &.selected": {
      backgroundColor: "#dae4e4",
      color: "#030712",
    },
  },
}));

const DateRangePickerContent = styled("div")(() => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",

  ".rdrDateDisplayWrapper": {
    backgroundColor: "#fff",
    order: 1,
    position: "absolute",
    width: "50%",
    height: "76px",
    bottom: 0,
    borderTop: "1px solid #eaecf0",

    "@media (max-width: 650px)": {
      display: "none",
    },

    ".rdrDateDisplay": {
      height: "100%",
      padding: "1rem",
      margin: 0,

      "& .rdrDateDisplayItem + .rdrDateDisplayItem": {
        marginLeft: "2rem",
      },

      "span.rdrDateDisplayItem:first-of-type::after": {
        content: `"-"`,
        position: "absolute",
        fontSize: "26px",
        height: "100%",
        color: "#667085",
        right: "-12px",
        transform: "translateX(100%)",
      },

      ".rdrDateInput": {
        border: "1px solid #e6e7e7",
        boxShadow: "0 1px 2px 0 #1018280d",
      },

      ".rdrDateInput input": {
        height: "100%",
        textAlign: "left",
        paddingLeft: "14px",
        color: "#101828",
      },

      ".rdrDateInput .rdrWarning": {
        top: "0.35rem",
      },
    },
  },

  ".rdrDayToday .rdrDayNumber span:after": {
    background: "#1cb4ba !important",
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    bottom: "0px",
  },

  "button.rdrDay.rdrDayHovered .rdrDayStartPreview.rdrDayEndPreview": {
    borderRadius: "50%",
    top: 0,
    bottom: 0,
  },

  ".rdrStartEdge, .rdrEndEdge": {
    background: "#052223 !important",
    width: "100%",
    height: "100%",
    top: 0,
    left: 0,
    borderRadius: "50%",
  },

  ".rdrInRange": {
    height: "100%",
    top: 0,
    background: "#dae4e4 !important",
  },

  ".rdrDayEndOfWeek .rdrInRange": {
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
    right: 0,
  },

  ".rdrDayStartOfWeek .rdrInRange": {
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
    left: 0,
  },

  ".rdrInRange ~ .rdrDayNumber span": {
    color: "#344054 !important",
    fontWeight: "400 !important",
  },

  ".rdrMonths": {
    "@media (max-width: 870px)": {
      justifyContent: "center",
    },
  },

  ".rdrMonth": {
    width: "300px",

    ".rdrMonthName": {
      textAlign: "center",
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: 600,
      color: "#030712",
      padding: "20px",
    },
  },

  ".rdrWeekDays": {
    paddingBottom: "4px",
  },

  ".rdrWeekDay": {
    width: "40px",
    height: "40px",
    lineHeight: "40px",
    flexBasis: "auto",
    color: "#344054",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "Inter",
  },

  ".rdrDays": {
    rowGap: "4px",
  },

  ".rdrDay": {
    aspectRatio: 1,
    width: "40px",
    height: "40px",
  },

  ".rdrDayNumber span": {
    fontSize: "14px",
    fontWeight: 400,
    fontFamily: "Inter",
    color: "#344054",
  },

  ".rdrDayPassive .rdrDayNumber span": {
    color: "#667085",
  },

  ".rdrMonthAndYearWrapper": {
    display: "flex",
    justifyContent: "center",
    position: "absolute",
    width: "100%",
  },

  ".rdrMonthAndYearPickers select": {
    opacity: 0,
    pointerEvents: "none",
  },

  ".rdrNextPrevButton": {
    background:
      "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAJUAAACUCAMAAACtIJvYAAAAY1BMVEX///8AAACXl5fi4uLX19fIyMhPT0/Nzc0JCQm3t7fw8PDFxcUYGBgODg7CwsKysrI7Ozunp6dBQUHp6ek0NDRKSkolJSVbW1v5+fmgoKCJiYmRkZF4eHgsLCyBgYFycnJoaGjSqllZAAACQklEQVR4nO3cbW+CQBAEYK5QRRSsUuV89///ykqaRsG7yV7SOGuy87lJJw/IHZfVLBOlWHVbd+qWhezPX5L2UrrflOeKXeYvjXuMEq7poJQrP9mF+uQLN4qGWpdxKVfP2Z2y4qmUBq1dqBVbyz9fQAVa1STcqqbWqtbhVlytmBVXyx9jrahawc8gXSsv47WIWuASErWqE6jF05qiVjyt+dOu4TFTVq0GteJpNVCLVwtq8S6iTq25aSUEapWmNYxWrfr9tHiLz0znmmhaKdGqhT6JvAfEO2rxlmqsRTukVKoFd6fEewu9VC+UavHuLdNKiGmlZIZamdYoppUSqLU1rWGwFq2Waf1bLZ7WUqkWWqpNaxTTSolppcS0UmJaKcFaOasW1Np4Vi2otWe1glp1S6uFtHa0VkjryGsFDiknzKHT6ODPmtiq+FJo1UZLuSvtiVWAoZ8PVikgxTv3Lr5BqY5UCkqxNjNQyjWcUm10xrQPabnBUitOKZOSBkuRHp8mJU2uUmpjUsJSJiUtZVLSUiql4CZPpdSBVMqkpKVUSkW/cUWUwq9YKqVIzyksRXpxMClpTEoanVJwP2VS91TwnmJt8kxKGJOSRqcUPApibYdNShiTkkanlMqXUZMSRqcULEUae1F5kuc1SvmrQik84cUapfLoK7u0cQk0t35mlcrOCqUy3ymUAtsXnhRoRZSKP62YUrfsg6WY84J9gt+3JkvdEjipYktloR+a4kvdctAn1Wd4wxPHdYdZ3lfo0+wV//AHtAMkIK3mVeUAAAAASUVORK5CYII=)",
    height: "12px",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    opacity: 0.75,

    "&.rdrPprevButton": {
      transform: "rotate(180deg)",
    },

    i: {
      display: "none",
    },
  },
}));

const ButtonsWrapper = styled("div")(() => ({
  width: "50%",
  height: "76px",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "12px",
  marginLeft: "auto",
  padding: "1rem",
  borderTop: "1px solid #eaecf0",
  bottom: 0,

  "@media (max-width: 650px)": {
    width: "100%",
  },
}));

interface CustomDateRangePickerProps {
  rangeValue?: Pick<Range, "startDate" | "endDate">;
  onDateSelect?: (range: Pick<Range, "startDate" | "endDate">) => void;
  onClose: () => void;
  anchorEl: null | Element;
}

const CustomDateRangePicker: FC<CustomDateRangePickerProps> = ({
  rangeValue,
  onDateSelect,
  onClose,
  anchorEl,
}): ReactElement => {
  const [state, setState] = useState<Range[]>(() => [
    {
      ...getLastYearToDateRange(),
      key: "selection",
    },
  ]);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [isAllTimeSelected, setIsAllTimeSelected] = useState<boolean>(false);
  const allTimePreset = useMemo(
    () => PRESET_RANGES.find((preset) => preset.isAllTime),
    []
  );

  const handleSelect = (ranges: RangeKeyDict) => {
    const newSelection = ranges.selection;
    const currentRange = state[0];
    
    // Determine if this is a partial update (only one date changed)
    const isStartDateOnly = newSelection.startDate && !newSelection.endDate;
    const isEndDateOnly = !newSelection.startDate && newSelection.endDate;
    
    // Handle date input changes more intelligently
    if (isStartDateOnly && currentRange.endDate && newSelection.startDate) {
      // User changed only start date - preserve end date
      const newStartDate = newSelection.startDate;
      const preservedEndDate = currentRange.endDate;
      
      // If start date is after end date, adjust end date to be same as start date
      const finalEndDate = newStartDate > preservedEndDate ? newStartDate : preservedEndDate;
      
      setState([{
        startDate: newStartDate,
        endDate: finalEndDate,
        key: "selection"
      }]);
    } else if (isEndDateOnly && currentRange.startDate && newSelection.endDate) {
      // User changed only end date - preserve start date
      const preservedStartDate = currentRange.startDate;
      const newEndDate = newSelection.endDate;
      
      // If end date is before start date, adjust start date to be same as end date
      const finalStartDate = newEndDate < preservedStartDate ? newEndDate : preservedStartDate;
      
      setState([{
        startDate: finalStartDate,
        endDate: newEndDate,
        key: "selection"
      }]);
    } else if (newSelection.startDate && newSelection.endDate) {
      // Both dates provided - use as is
      setState([newSelection]);
    } else {
      // Fallback for any other case
      setState([newSelection]);
    }
    
    setSelectedPreset(null); // Reset preset selection
    setIsAllTimeSelected(false);
  };

  const setPresetRange = (preset: { label: string; range: Range; isAllTime?: boolean }) => {
    setState([
      { ...state[0], startDate: preset.range.startDate, endDate: preset.range.endDate },
    ]);
    setSelectedPreset(preset.label);
    setIsAllTimeSelected(Boolean(preset.isAllTime));
  };

  useEffect(() => {
    if (!rangeValue || (!rangeValue.startDate && !rangeValue.endDate)) {
      const fallbackRange = allTimePreset?.range;
      setSelectedPreset(allTimePreset?.label ?? "All Time");
      setIsAllTimeSelected(true);
      setState([
        {
          startDate: fallbackRange?.startDate,
          endDate: fallbackRange?.endDate,
          key: "selection",
        },
      ]);
      return;
    }

    setIsAllTimeSelected(false);
    setState([{ ...rangeValue, key: "selection" }]);

    const matchingPreset = PRESET_RANGES.find((preset) => {
      if (preset.isAllTime) return false;

      const presetStart = preset.range.startDate?.toDateString();
      const presetEnd = preset.range.endDate?.toDateString();
      const valueStart = rangeValue.startDate?.toDateString();
      const valueEnd = rangeValue.endDate?.toDateString();

      return presetStart === valueStart && presetEnd === valueEnd;
    });

    setSelectedPreset(matchingPreset?.label ?? null);
  }, [rangeValue, allTimePreset]);

  return (
    <DialogMenu
      sx={{
        "& .MuiPaper-root": {
          width: {
            xs: "100%",
            md: "initial",
          },
        },
      }}
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <DateRangePickerWrapper>
        <Sidebar>
          <Box
            component="ul"
            sx={{
              xs: {
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
              },
            }}
          >
            {PRESET_RANGES.map((preset) => (
              <li key={preset.label}>
                <button
                  className={selectedPreset === preset.label ? "selected" : ""}
                  onClick={() => setPresetRange(preset)}
                >
                  {preset.label}
                </button>
              </li>
            ))}
          </Box>
        </Sidebar>
        <DateRangePickerContent>
          <DateRange
            editableDateInputs
            onChange={handleSelect}
            moveRangeOnFirstSelection={false}
            ranges={state}
            maxDate={new Date()}
            months={window.innerWidth < 635 ? 1 : 2}
            direction="horizontal"
            showMonthAndYearPickers={true}
            showDateDisplay={true}
            rangeColors={["#052223"]}
            retainEndDateOnFirstSelection={true}
          />
          <ButtonsWrapper>
            <Button
              variant="white-outlined"
              sx={{
                fontSize: 14,
                fontWeight: 500,
                padding: "10 1rem",
                maxHeight: 40,
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (isAllTimeSelected) {
                  onDateSelect?.({ startDate: undefined, endDate: undefined });
                } else {
                  onDateSelect?.(state[0]);
                }
                onClose();
              }}
            >
              Apply
            </Button>
          </ButtonsWrapper>
        </DateRangePickerContent>
      </DateRangePickerWrapper>
    </DialogMenu>
  );
};

export default CustomDateRangePicker;
